import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTravels } from '../TravelContext';
import { v4 as uuidv4 } from 'uuid';
import GoogleAdUnit from '../components/common/GoogleAdUnit';

// Modern (glass) yan panel: seçilen şehir için tarih, hava, etkinlik, haber, kur ve saat
const RightInfoPanel = ({ city = 'İstanbul' }) => {
  const [ctx, setCtx] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // basit geocode (Open‑Meteo; anahtar gerektirmez)
  const geocode = async (name) => {
    const r = await fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(name)}&count=1&language=tr&format=json`);
    if (!r.ok) throw new Error('Geocode failed');
    const j = await r.json();
    const first = j.results?.[0];
    return first ? { lat: first.latitude, lon: first.longitude } : { lat: 41.0082, lon: 28.9784 }; // İstanbul varsayılan
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const { lat, lon } = await geocode(city);
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
        const [history, weather, events, news, currency, time] = await Promise.all([
          fetch(`${API_BASE_URL}/api/history?query=${encodeURIComponent(city)}`).then(r => r.json()).catch(() => ({})),
          fetch(`${API_BASE_URL}/api/weather?lat=${lat}&lon=${lon}`).then(r => r.json()).catch(() => ({})),
          fetch(`${API_BASE_URL}/api/events?city=${encodeURIComponent(city)}`).then(r => r.json()).catch(() => ({})),
          fetch(`${API_BASE_URL}/api/news?q=${encodeURIComponent(city)}&lang=tr`).then(r => r.json()).catch(() => ({})),
          fetch(`${API_BASE_URL}/api/currency?base=TRY&symbols=USD,EUR`).then(r => r.json()).catch(() => ({})),
          fetch(`${API_BASE_URL}/api/time?timezone=Europe/Istanbul`).then(r => r.json()).catch(() => ({}))
        ]);
        if (!active) return;
        setCtx({ history, weather, events, news, currency, time });
        setLoading(false);
      } catch (e) {
        if (!active) return;
        setErr(e.message);
        setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [city]);

  // Glassmorphism card styles using Memory Bank colors
  const cardCls = 'rounded-2xl p-4 md:p-5 bg-background-light/60 dark:bg-white/10 backdrop-blur-xl border border-white/20 shadow-sm text-text-dark dark:text-white';
  const headerCls = 'font-semibold text-text-dark dark:text-white text-base mb-2';

  // veri çıkarımları
  const hist = ctx.history?.data;
  const histExtract = hist?.extract;
  const weatherData = ctx.weather?.data;
  const currentTemp = weatherData?.current_weather?.temperature;
  const eventsItems = ctx.events?.data?._embedded?.events || [];
  const newsItems = ctx.news?.data?.articles || ctx.news?.data?.articles?.results || ctx.news?.data || [];
  const rates = ctx.currency?.data?.rates;
  const timeStr = ctx.time?.data?.datetime || ctx.time?.data?.datetime_str || ctx.time?.data?.datetime || ctx.time?.data?.utc_datetime;

  return (
    <aside className="md:col-span-1 w-full md:sticky md:top-20 space-y-4 md:space-y-5">
      <div className="rounded-2xl p-4 md:p-5 bg-primary/90 text-white shadow-md backdrop-blur-sm">
        <h3 className="text-xl font-bold">Şehir Bilgisi</h3>
        <p className="opacity-90 text-sm">Güncel özetler ve canlı veriler</p>
      </div>
      {loading && (
        <div className={cardCls}>Veriler yükleniyor…</div>
      )}
      {err && (
        <div className={cardCls}>Hata: {err}</div>
      )}
      {!loading && !err && (
        <>
          <div className={cardCls}>
            <div className={headerCls}>Tarihçe</div>
            <div className="text-sm leading-relaxed">{histExtract ? histExtract : 'Özet bulunamadı.'}</div>
          </div>

          <div className={cardCls}>
            <div className={headerCls}>Hava Durumu</div>
            <div className="text-sm">Anlık Sıcaklık: {currentTemp != null ? `${currentTemp}°C` : '—'}</div>
          </div>

          <div className={cardCls}>
            <div className={headerCls}>Etkinlikler</div>
            {eventsItems.length === 0 ? (
              <div className="text-sm">Etkinlik verisi bulunamadı (anahtar gerekli olabilir).</div>
            ) : (
              <ul className="space-y-2 text-sm list-disc ml-5">
                {eventsItems.slice(0, 5).map(ev => (
                  <li key={ev.id}>{ev.name}</li>
                ))}
              </ul>
            )}
          </div>

          <div className={cardCls}>
            <div className={headerCls}>Haberler</div>
            {Array.isArray(newsItems) && newsItems.length > 0 ? (
              <ul className="space-y-2 text-sm list-disc ml-5">
                {newsItems.slice(0, 5).map((n, i) => (
                  <li key={n.url || i}>
                    {n.title || n.name || 'Başlık yok'}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-sm">Haber verisi bulunamadı (anahtar gerekli olabilir).</div>
            )}
          </div>

          <div className={cardCls}>
            <div className={headerCls}>Döviz</div>
            <div className="text-sm">USD: {rates?.USD ? rates.USD.toFixed(2) : '—'}</div>
            <div className="text-sm">EUR: {rates?.EUR ? rates.EUR.toFixed(2) : '—'}</div>
          </div>

          <div className={cardCls}>
            <div className={headerCls}>Saat</div>
            <div className="text-sm">{timeStr ? new Date(timeStr).toLocaleString('tr-TR') : '—'}</div>
          </div>

          {/* Google Vertical Ad */}
          <div className="rounded-2xl p-4 bg-white/50 dark:bg-black/20 backdrop-blur-sm border border-white/10 shadow-sm flex justify-center">
            <GoogleAdUnit />
          </div>
        </>
      )}
    </aside>
  );
};

const PlanResultPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { addTravel } = useTravels();

  // location.state'den gelen veriyi alıyoruz.
  const [plan, setPlan] = useState(location.state?.plan);
  const [query, setQuery] = useState(location.state?.query);
  const [isLoading, setIsLoading] = useState(true); // Başlangıçta yükleniyor

  useEffect(() => {
    // Eğer state ile veri gelmemişse, ana sayfaya yönlendir.
    if (!location.state?.plan || !location.state?.query) {
      setIsLoading(false);
      return;
    }

    // Veri geldiyse, state'leri güncelle ve yüklenmeyi bitir.
    const { plan: planData, query: queryData } = location.state;
    setPlan(planData);
    setQuery(queryData);
    setIsLoading(false);

    // Planı ve hayali localStorage'a kaydetme mantığı
    try {
      const travelObj = {
        id: uuidv4(),
        route: `${queryData.origin} - ${queryData.destination}`,
        nextStop: planData.gunluk_plan?.[0]?.baslik || 'Başlangıç',
        details: {
          // Calculate days from the generated plan's daily plan length
          days: `${planData.gunluk_plan ? planData.gunluk_plan.length : 0} gün`,
          budget: `${queryData.budget} TL`,
          planSummary: planData.ozet,
          gunlukPlan: planData.gunluk_plan,
          konaklamaOnerileri: planData.konaklama_onerileri,
          restoranOnerileri: planData.restoran_onerileri,
          genel_tavsiyeler: planData.genel_tavsiyeler || []
        }
      };

      const existingTravels = JSON.parse(localStorage.getItem('seyHot_travels') || '[]');
      const travelExists = existingTravels.some(t => t.route === travelObj.route && t.details.planSummary === travelObj.details.planSummary);
      if (!travelExists) {
        addTravel(travelObj);
      }

      const dreamToAdd = {
        id: uuidv4(),
        destination: queryData.destination,
        date: new Date().toISOString().slice(0, 10),
        budget: queryData.budget
      };

      const prevDreams = JSON.parse(localStorage.getItem('seyHot_dreams') || '[]');
      const dreamExists = prevDreams.some(d => d.destination === dreamToAdd.destination);
      if (!dreamExists) {
        localStorage.setItem('seyHot_dreams', JSON.stringify([...prevDreams, dreamToAdd]));
      }

    } catch (e) {
      console.error('Plan kaydetme hatası:', e);
    }

  }, [location.state, addTravel]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-2xl font-semibold">Planınız Yükleniyor...</div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="text-center p-8">
        <h2 className="text-2xl font-bold mb-4">Plan bulunamadı.</h2>
        <p className="mb-4">Görünüşe göre bu sayfaya doğrudan eriştiniz veya bir hata oluştu.</p>
        <button
          onClick={() => navigate('/')}
          className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition-colors"
        >
          Anasayfaya Dön
        </button>
      </div>
    );
  }

  // Planı + sağ paneli göstermek için düzen
  const cityName = (query?.destination || '').toString() || 'İstanbul';

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8">
      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 dark:text-white mb-4">
            İşte Seyahat Planın: {query.origin} → {query.destination}
          </h1>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 mb-8">
            <h2 className="text-2xl font-bold mb-3">Özet</h2>
            <p className="text-gray-600 dark:text-gray-400">{plan.ozet}</p>
            <p className="mt-4 font-semibold">Tahmini Toplam Maliyet: <span className="text-blue-600">{plan.tahmini_toplam_maliyet} TL</span></p>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-4">Günlük Plan</h2>
            <div className="space-y-6">
              {Array.isArray(plan.gunluk_plan) && plan.gunluk_plan.map((day, index) => (
                <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                  <h3 className="font-bold text-xl text-blue-700 dark:text-blue-400">
                    {typeof day.gun === 'number' || !isNaN(day.gun) ? `${day.gun}. Gün` : `Gün ${index + 1}`}: {day.baslik || ''}
                  </h3>
                  <p className="mt-2 text-gray-700 dark:text-gray-300">
                    {day.icerik || 'Içerik'}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-2xl font-bold mb-4">Konaklama Önerileri</h2>
              {Array.isArray(plan.konaklama_onerileri) && plan.konaklama_onerileri.length > 0 ? (
                <div className="space-y-4">
                  {plan.konaklama_onerileri.map((hotel, i) => (
                    <div key={i} className="border-l-4 border-blue-500 pl-4">
                      <p className="font-semibold text-gray-800 dark:text-white">{hotel.isim || 'Otel'}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Tip: {hotel.tip}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Fiyat: {hotel.fiyat}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Adres: {hotel.adres}</p>
                      {Array.isArray(hotel.ozellikler) && hotel.ozellikler.length > 0 && (
                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                          Özellikleri: {hotel.ozellikler.join(', ')}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">Konaklama önerisi bulunamadı.</p>
              )}
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-2xl font-bold mb-4">Restoran Önerileri</h2>
              {Array.isArray(plan.restoran_onerileri) && plan.restoran_onerileri.length > 0 ? (
                <div className="space-y-4">
                  {plan.restoran_onerileri.map((restaurant, i) => (
                    <div key={i} className="border-l-4 border-orange-500 pl-4">
                      <p className="font-semibold text-gray-800 dark:text-white">{restaurant.isim || 'Restoran'}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Mutfak: {restaurant.mutfak}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Fiyat Aralığı: {restaurant.fiyat_aralik}</p>
                      {Array.isArray(restaurant.onerilen) && restaurant.onerilen.length > 0 && (
                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                          Önerilen: {restaurant.onerilen.join(', ')}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">Restoran önerisi bulunamadı.</p>
              )}
            </div>
          </div>
        </div>
        {/* Sağ pop‑art panel */}
        <RightInfoPanel city={cityName} />
      </div>
    </div>
  );
};

export default PlanResultPage;